import api from 'services/api';
import { array, enforce, nullable, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/folders.js`
// GET /folders. The full response is `{ folders, pagination, config,
// filtersConfig }` and each folder carries more fields than an LOV row needs
// (description, zone_id, child_count, timestamps). The projection narrows to
// the identifiers required to render a row and resolve its full path.
export interface FolderSummary {
  readonly folder_id: number;
  readonly name: string;
  readonly zone_name: string | null;
  readonly parent_folder_id: number | null;
}

const summarySchema = object<FolderSummary>({
  folder_id: num(),
  name: str(),
  zone_name: nullable(str()),
  parent_folder_id: nullable(num()),
});

interface FoldersResponse {
  readonly folders: readonly FolderSummary[];
}

const responseSchema = object<FoldersResponse>({
  folders: array(summarySchema),
});

const LOV_LIMIT = 2000;

/**
 * Fetch the complete flat folder list for dropdowns/typeaheads. No pagination:
 * the LOV needs every folder so paths resolve and client-side filtering has the
 * full set. If the folder universe ever outgrows a usable dropdown, this is the
 * call site to migrate to server-side search.
 */
export const getFoldersLOV = async (): Promise<readonly FolderSummary[]> => {
  const res = await api.get('/folders', {
    params: { limit: LOV_LIMIT, root_only: false },
  });
  const parsed = runSchema(responseSchema, res.data, 'getFoldersLOV');
  if (!parsed.ok) {
    throw new Error('getFoldersLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['folders']).folders;
};
